import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../auth/api';
import { useAuth } from '../../auth/AuthContext';
import useFaceMesh from './useFaceMesh';
import useAudio, { scoreSound } from './useAudio';
import SkyDecor from '../../components/SkyDecor';
import Confetti from '../../components/Confetti';
import {
  computeMouthMetrics,
  computeMatchScore,
  getHint,
  LIP_OUTER_LOOP,
  LIP_INNER_LOOP,
} from './shapeDetector';
import './OralMotorPage.css';

// Shape target emoji/animations
const SHAPE_VISUALS = {
  wide:      { emoji: '😁', label: 'Smile Wide' },
  round:     { emoji: '😮', label: 'Round Lips' },
  open:      { emoji: '😲', label: 'Open Wide' },
  tongue_up: { emoji: '😛', label: 'Tongue Tip Up' },
  closed:    { emoji: '😶', label: 'Lip Press' },
  puff:      { emoji: '🐡', label: 'Puff Cheeks' },
};

const MATCH_STREAK_NEEDED = 8; // consecutive good frames to confirm match

// Per-shape camera thresholds — some shapes can't reach high scores
// because MediaPipe can't see tongue/cheeks/air directly
const CAMERA_THRESHOLD = {
  wide:      0.72,
  round:     0.72,
  open:      0.72,
  tongue_up: 0.40,  // MediaPipe can't see tongue — lower threshold
  closed:    0.60,  // tight lips are hard to measure precisely
  puff:      0.55,  // MediaPipe can't see air in cheeks
};

export default function OralMotorPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { childId, childName } = location.state || {};

  // Exercise state
  const [exercises, setExercises] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Camera state
  const videoRef = useRef(null);
  const lipCanvasRef = useRef(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);

  // Detection state — camera
  const { landmarks, blendshapes, isLoaded: modelLoaded } = useFaceMesh(videoRef, cameraActive);
  const [visualScore, setVisualScore] = useState(0);
  const [hint, setHint] = useState('');

  // Detection state — sound
  const { isReady: audioReady, getSoundMetrics } = useAudio(cameraActive);
  const [soundResult, setSoundResult] = useState({ status: 'waiting', label: 'Waiting for sound…' });
  const [soundMetrics, setSoundMetrics] = useState(null);

  // Combined match state
  const [shapeComplete, setShapeComplete] = useState(false);
  const [streak, setStreak] = useState(0);
  const streakRef = useRef(0);

  // Session tracking
  const [results, setResults] = useState([]);
  const [showCelebration, setShowCelebration] = useState(false);

  const currentExercise = exercises[currentIndex] || null;

  // Does current exercise need sound matching?
  const needsSound = currentExercise?.target_f1_min != null;

  // ── Load exercises from backend ─────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        // First get all exercises
        const res = await api.get('/oral-motor/exercises');
        let allEx = res.data;
        if (allEx.length === 0) {
          // Seed if empty
          await api.post('/oral-motor/seed');
          const res2 = await api.get('/oral-motor/exercises');
          allEx = res2.data;
        }

        // Then check if the child has specific assigned exercises
        if (childId) {
          try {
            const assignRes = await api.get(`/oral-motor/assignments/${childId}`);
            if (assignRes.data && assignRes.data.length > 0) {
              const latestAssignment = assignRes.data[0];
              const assignedIds = JSON.parse(latestAssignment.exercise_ids);
              const assignedEx = allEx.filter(ex => assignedIds.includes(ex.id));
              
              if (assignedEx.length > 0) {
                setExercises(assignedEx);
                return;
              }
            }
          } catch (e) {
            console.error('Failed to load assignments, using defaults:', e);
          }
        }

        // Fallback to all exercises if no active assignment
        setExercises(allEx);
      } catch (err) {
        console.error('Failed to load exercises:', err);
        setError('Failed to load exercises');
      } finally {
        setLoading(false);
      }
    })();
  }, [childId]);

  // ── Start camera ────────────────────────────────────────────────
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 320, height: 240 },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setCameraReady(true);
        setCameraActive(true);
      }
    } catch (err) {
      console.error('Camera error:', err);
      setError('Camera permission needed! Please allow camera access.');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    setCameraReady(false);
  }, []);

  // Start camera when exercises load
  useEffect(() => {
    if (exercises.length > 0 && !cameraReady) {
      startCamera();
    }
    return () => stopCamera();
  }, [exercises.length]);

  // ── Per-frame detection: Camera + Sound + Combined ──────────────
  useEffect(() => {
    if (!landmarks || !currentExercise || shapeComplete) return;

    // 1. Camera — compute visual score (0–1)
    const metrics = computeMouthMetrics(landmarks, blendshapes);
    const vScore = metrics ? computeMatchScore(currentExercise.target_shape_type, metrics) : 0;
    setVisualScore(vScore);
    setHint(getHint(currentExercise.target_shape_type, vScore));

    // 2. Sound — get formant metrics and score
    const sMetrics = getSoundMetrics();
    setSoundMetrics(sMetrics);
    const sResult = scoreSound(currentExercise, sMetrics);
    setSoundResult(sResult);

    // 3. Combined gate
    const camThreshold = CAMERA_THRESHOLD[currentExercise.target_shape_type] || 0.72;
    const visualGood = vScore > camThreshold;
    const soundGood = sResult.status === 'good';
    const matched = needsSound ? (visualGood && soundGood) : visualGood;

    // 4. Streak counter
    if (matched) {
      streakRef.current++;
    } else {
      streakRef.current = 0;
    }
    setStreak(streakRef.current);

    if (streakRef.current >= MATCH_STREAK_NEEDED) {
      setShapeComplete(true);
      streakRef.current = 0;
    }
  }, [landmarks, blendshapes, currentExercise, shapeComplete, needsSound, getSoundMetrics]);

  // ── Draw lip-tracking overlay ───────────────────────────────────
  useEffect(() => {
    const canvas = lipCanvasRef.current;
    const video = videoRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    // Match the canvas backing store to the video's native resolution so
    // normalised landmarks (0–1) map linearly to pixels. The canvas is
    // CSS-mirrored to align with the selfie-view video.
    const w = video?.videoWidth || 320;
    const h = video?.videoHeight || 240;
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Nothing to draw until the model is ready and a face is detected.
    if (!modelLoaded || !landmarks) return;

    const camThreshold =
      CAMERA_THRESHOLD[currentExercise?.target_shape_type] || 0.72;
    const color = shapeComplete
      ? '#4CAF50'                                   // matched → green
      : visualScore > camThreshold
        ? '#8BC34A'                                 // almost there → light green
        : '#FFD400';                                // trying → yellow

    const drawLoop = (indices, lineWidth) => {
      ctx.beginPath();
      indices.forEach((idx, i) => {
        const pt = landmarks[idx];
        if (!pt) return;
        const x = pt.x * canvas.width;
        const y = pt.y * canvas.height;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.closePath();
      ctx.lineWidth = lineWidth;
      ctx.strokeStyle = color;
      ctx.lineJoin = 'round';
      ctx.stroke();
    };

    // Soft glow behind the outline for a friendly "highlight" feel.
    ctx.shadowColor = color;
    ctx.shadowBlur = 8;
    drawLoop(LIP_OUTER_LOOP, 2);
    ctx.shadowBlur = 0;
    drawLoop(LIP_INNER_LOOP, 1.5);
  }, [landmarks, modelLoaded, shapeComplete, visualScore, currentExercise]);

  // ── Navigation ──────────────────────────────────────────────────
  const handleNext = () => {
    setResults(prev => [...prev, {
      shape_name: currentExercise.shape_name,
      mouth_matched: shapeComplete,
      sound_matched: soundResult.status === 'good' && needsSound,
    }]);

    if (currentIndex < exercises.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setShapeComplete(false);
      setStreak(0);
      streakRef.current = 0;
      setVisualScore(0);
      setSoundResult({ status: 'waiting', label: 'Waiting for sound…' });
      setSoundMetrics(null);
      setHint('');
    } else {
      completeSession();
    }
  };

  const completeSession = async () => {
    const finalResults = [...results, {
      shape_name: currentExercise.shape_name,
      mouth_matched: shapeComplete,
      sound_matched: soundResult.status === 'good' && needsSound,
    }];

    setShowCelebration(true);
    stopCamera();

    try {
      await api.post('/oral-motor/session/complete', {
        child_id: childId,
        total_shapes: exercises.length,
        completed_shapes: finalResults.length,
        mouth_match_count: finalResults.filter(r => r.mouth_matched).length,
        sound_match_count: finalResults.filter(r => r.sound_matched).length,
        details: JSON.stringify(finalResults),
      });
    } catch (err) {
      console.error('Failed to save session:', err);
    }
  };

  // ── Render ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="om-container sb-page">
        <div className="om-loading">
          <div className="om-loading-spinner"></div>
          <p>Loading Mouth Gym...</p>
        </div>
      </div>
    );
  }

  if (showCelebration) {
    const matchCount = [...results, { mouth_matched: shapeComplete }].filter(r => r.mouth_matched).length;
    return (
      <div className="om-container sb-page">
        <SkyDecor />
        <Confetti active burstKey="finale" count={36} />
        <div className="om-celebration">
          <div className="om-trophy">🏆</div>
          <h1>Great Job{childName ? `, ${childName}` : ''}!</h1>
          <p>You matched <strong>{matchCount}</strong> out of <strong>{exercises.length}</strong> shapes!</p>
          <div className="om-stars">
            {exercises.map((_, i) => (
              <span key={i} className="om-star">{i < matchCount ? '⭐' : '☆'}</span>
            ))}
          </div>
          <button className="om-btn om-btn-primary" onClick={() => navigate('/child/hub')}>
            Back to Hub 🏠
          </button>
        </div>
      </div>
    );
  }

  if (!currentExercise) {
    return (
      <div className="om-container sb-page">
        <p>No exercises available. Please ask your therapist to set them up.</p>
        <button className="om-btn" onClick={() => navigate('/child/hub')}>← Back</button>
      </div>
    );
  }

  const visual = SHAPE_VISUALS[currentExercise.target_shape_type] || { emoji: '👄', label: currentExercise.shape_name };
  const visualPct = Math.round(visualScore * 100);
  const streakPct = Math.round((streak / MATCH_STREAK_NEEDED) * 100);

  return (
    <div className="om-container sb-page">
      <SkyDecor />
      <Confetti active={shapeComplete} burstKey={currentIndex} />
      {/* Navbar */}
      <div className="om-navbar">
        <button className="om-btn om-btn-back" onClick={() => navigate('/child/hub')}>← Hub</button>
        <div className="om-nav-title">Mouth Gym 👄</div>
        <button className="om-btn om-btn-logout" onClick={logout}>Logout</button>
      </div>

      {/* Progress dots */}
      <div className="om-progress-bar">
        <span className="om-progress-label">{currentIndex + 1} / {exercises.length} shapes</span>
        <div className="om-dots">
          {exercises.map((_, i) => (
            <div key={i} className={`om-dot ${i < currentIndex ? 'done' : i === currentIndex ? 'active' : ''}`}>
              {i < currentIndex ? '✓' : i + 1}
            </div>
          ))}
        </div>
      </div>

      <div className="om-main">
        {/* Left: Target shape */}
        <div className="om-target-section">
          <div className="om-shape-card">
            <div className="om-shape-emoji">{visual.emoji}</div>
            <h2 className="om-shape-name">{currentExercise.shape_name}</h2>
            {currentExercise.sound_text && (
              <p className="om-sound-text">Say: <strong>{currentExercise.sound_text}</strong></p>
            )}
            <p className="om-instruction">{currentExercise.instruction}</p>
            {currentExercise.helper_check && (
              <div className="om-helper-tip">
                💡 <em>{currentExercise.helper_check}</em>
              </div>
            )}
          </div>
        </div>

        {/* Right: Camera + feedback */}
        <div className="om-camera-section">
          <div className="om-camera-wrap">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="om-video"
            />
            <canvas ref={lipCanvasRef} className="om-lip-canvas" />
            {!modelLoaded && cameraActive && (
              <div className="om-camera-overlay">
                <div className="om-loading-spinner small"></div>
                <p>Loading face detector...</p>
              </div>
            )}
            {cameraReady && (
              <div className="om-mouth-label">MOUTH HERE</div>
            )}
          </div>

          {/* Feedback cards */}
          <div className="om-feedback-area">
            {/* Camera Check */}
            <div className={`om-check-card ${shapeComplete ? 'success' : visualScore > 0.72 ? 'trying' : ''}`}>
              <div className="om-check-header">📷 CAMERA CHECK</div>
              {shapeComplete ? (
                <div className="om-check-body success">
                  ✅ <strong>Shape matched!</strong>
                </div>
              ) : (
                <div className="om-check-body">
                  <div className="om-score-bar">
                    <div
                      className={`om-score-fill ${visualPct > 72 ? 'good' : visualPct > 50 ? 'mid' : ''}`}
                      style={{ width: `${visualPct}%` }}
                    ></div>
                  </div>
                  <p className="om-score-label">{visualPct}% — {hint}</p>
                </div>
              )}
            </div>

            {/* Sound Check — only for vowel shapes */}
            {needsSound && (
              <div className={`om-check-card ${soundResult.status === 'good' ? 'success' : soundResult.status === 'try' ? 'trying' : ''}`}>
                <div className="om-check-header">🔊 SOUND CHECK</div>
                <div className="om-check-body">
                  <p>{soundResult.label}</p>
                  {soundMetrics && soundMetrics.energy > 14 && (
                    <span className="om-formant-info">
                      F1: {Math.round(soundMetrics.f1)} Hz · F2: {Math.round(soundMetrics.f2)} Hz
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Streak progress */}
            {!shapeComplete && (visualScore > 0.72 || streak > 0) && (
              <div className="om-check-card streak">
                <div className="om-check-header">🎯 HOLD STREAK</div>
                <div className="om-check-body">
                  <div className="om-hold-bar">
                    <div className="om-hold-fill" style={{ width: `${streakPct}%` }}></div>
                  </div>
                  <p>{streak} / {MATCH_STREAK_NEEDED} frames</p>
                </div>
              </div>
            )}

            {error && <div className="om-error">{error}</div>}
          </div>
        </div>
      </div>

      {/* Bottom buttons */}
      <div className="om-bottom-bar">
        <button
          className={`om-btn ${shapeComplete ? 'om-btn-matched' : ''}`}
          disabled
        >
          {shapeComplete ? '✅ Shape Complete!' : '⏳ Waiting...'}
        </button>
        <button
          className="om-btn om-btn-primary"
          onClick={handleNext}
        >
          {currentIndex < exercises.length - 1 ? 'Next Shape →' : 'Finish! 🎉'}
        </button>
      </div>
    </div>
  );
}
